<template>

  <section class="rta-completion" :data-mode="mode">

    <!-- Completed (UX spec 2.8, mock M-28b / c): the Completed panel and its View site / View RTS are the drawer's (RtaDetails). What is left for
         Step 5 is the Final option (the one that became the site) and the Completion Remarks; the captured values are not shown. -->
    <template v-if="mode === 'completed'">
      <rta-option-summary
        heading="Final option"
        :rows="finalRows"
        :option-id="finalOptionIdForView"
        :option-no="optionNo" />
      <read-only-remarks v-if="completionRemarks" label="Completion Remarks" :html="rta.closeReason" />
    </template>

    <template v-else>

      <!-- The client's view (UX v3 section 6) is the final option alone: no values, no lock line, no forms. The API never sends it
           state.completion. It has no waiting alert (decision 166): the drawer's status panel already says "{Tower Co} is setting up the
           site from option {n}." -->
      <template v-if="mode !== 'client'">
        <!-- The lock line of a locked approval (Submit decision locked it). Unlock is the only way back to Step 4, and it is the
             drawer's command: it flushes this form first and keeps what was entered (decision 46). -->
        <approval-lock-line
          ref="lockLine"
          placement="end"
          :row="lockRow"
          :title="'RTA #' + rta.id"
          :decided="true"
          :can-unlock="canUnlock"
          :busy="lockBusy !== null"
          :busy-label="lockBusyText"
          :format="formatDateTime"
          text-id="rta-completion-lock-line"
          @unlock="$emit('unlock')" />

        <p v-if="!canEdit" class="rta-completion__note"><small id="rta-completion-readonly-note" class="text-muted"><i class="fa fa-lock" aria-hidden="true"></i>&nbsp;{{ readOnlyNote }}</small></p>
      </template>

      <!-- The final option, read-only: what the site and the RTS inherit (RtaOptionSummary, as Step 4's "Approved option"). -->
      <rta-option-summary
        heading="Final option"
        :rows="finalRows"
        :option-id="finalOptionIdForView"
        :option-no="optionNo" />

      <template v-if="mode === 'tower'">

        <!-- The completion state could not be read (the defaults come from the server). -->
        <el-alert
          v-if="loadFailed"
          class="rta-completion__failed"
          type="warning"
          show-icon
          :closable="false"
          title="Step 5 couldn't be loaded."
          description="The site and RTS details come from the server. Try again, or reload the page.">
          <el-button size="mini" @click="retryLoad">Try again</el-button>
        </el-alert>

        <p v-else-if="!form || !ready" class="text-muted rta-completion__loading"><small>Loading Step 5…</small></p>

        <template v-else>

          <!-- Precheck and server errors, listed (UX v3 section 6). It takes focus when Complete RTA found something to fix. -->
          <el-alert
            v-if="errorCount > 0"
            ref="errorAlert"
            class="rta-completion__errors"
            type="error"
            show-icon
            :closable="false"
            tabindex="-1"
            :title="errorTitle" />

          <h5>New site</h5>
          <site-form
            :key="formKey"
            v-model="form"
            capture
            handover-transition
            :transitioned-status="transitionedStatus"
            :transitioned-handover="transitionedHandover"
            :identity-read-only="identityReadOnly"
            :site-name-note="siteNameNote"
            :site-id-note="siteIdNote"
            :handover-status-note="handoverStatusNote"
            :read-only="!canEdit"
            :errors="errors"
            :tab-errors="tabErr"
            :active-tab.sync="tab"
            @input="onSiteInput" />

          <!-- XOL-693 (decision 211): the RTS section wraps all three variants. A new RTS is opened only when the client has no blocking sharer on the
               site and no open RTS there (A: today's form). Otherwise the API sends `completion.rtsChoice` and there are two choices, link the
               client's existing RTS or complete without one (B); with nothing to link there is only a note (C). -->
          <div v-if="!tcClient" ref="rtsSection" class="rta-rts" role="group" tabindex="-1" :aria-labelledby="rtsDomIds.heading">
            <h5 :id="rtsDomIds.heading">RTS for {{ names.telco }}</h5>

            <template v-if="!choice">
              <p class="rta-rts-line"><b>Sharer: {{ names.telco }} · Site: {{ siteLabel }}{{ keptSite ? '' : ' (being created)' }} · Type: RTS</b></p>
              <el-form :model="rts" label-position="top" class="rta-rts-form" :class="{ 'rta-rts-form--readonly': !canEdit }" @submit.native.prevent>
                <request-create-fields
                  :key="formKey"
                  :value="rts"
                  :read-only="!canEdit"
                  :disabled="!canEdit"
                  :errors="rtsErrors"
                  :summary-max="summaryMax"
                  @input="onRtsInput" />
              </el-form>
            </template>

            <el-alert
              v-else-if="!choice.candidate"
              class="rta-rts-none"
              type="info"
              show-icon
              :closable="false"
              title="No RTS is created">
              <span>{{ rtsNoneText }}</span>
              <span v-if="choice.claimed" class="rta-rts-none__acts">
                <el-button
                  type="primary"
                  size="mini"
                  round
                  icon="fa fa-chevron-right"
                  class="rta-view-btn"
                  :aria-label="'View ' + rtsRef(choice.claimed.id)"
                  @click="viewRts(choice.claimed.id)">View RTS</el-button>
              </span>
            </el-alert>

            <div v-else>
              <small :id="rtsDomIds.why" class="rta-rts-choice__note">{{ rtsWhy }}</small>
              <el-form label-position="top" class="rta-rts-choice" @submit.native.prevent>
                <el-form-item label="RTS on completion" :error="rtsErrors.linkRtsId || null">
                  <el-radio-group
                    v-if="canEdit"
                    size="small"
                    :value="rtsMode"
                    aria-label="RTS on completion"
                    :aria-describedby="rtsDomIds.why + ' ' + rtsDomIds.outcome"
                    @input="onRtsMode">
                    <el-radio-button label="link">Link the existing RTS</el-radio-button>
                    <el-radio-button label="none">Complete without an RTS</el-radio-button>
                  </el-radio-group>
                  <b v-else>{{ rtsMode === 'link' ? 'Link the existing RTS' : 'Complete without an RTS' }}</b>
                </el-form-item>
              </el-form>
              <small :id="rtsDomIds.outcome" class="rta-rts-choice__note">{{ rtsHint }}</small>

              <rta-option-summary
                v-if="rtsMode === 'link'"
                class="rta-rts-card"
                :heading="rtsRef(choice.candidate.id)"
                :rows="rtsCardRows"
                :option-id="null">
                <el-button
                  type="primary"
                  size="mini"
                  round
                  icon="fa fa-chevron-right"
                  class="rta-view-btn"
                  :aria-label="'View ' + rtsRef(choice.candidate.id)"
                  @click="viewRts(choice.candidate.id)">View RTS</el-button>
              </rta-option-summary>
            </div>
          </div>

          <el-alert
            v-else
            class="rta-completion__no-rts"
            type="info"
            show-icon
            :closable="false"
            title="No RTS is created"
            :description="names.towerCo + ' is its own client, so completing this RTA creates the site only.'" />

        </template>
      </template>

    </template>

  </section>

</template>


<script>

/*
 * XOL-67 (B6, v2.1 V6): Step 5, Finalize (plan 1.5, UX spec 2.8, decisions 40, 45, 46, 153, 158 Q3, 161). Three audiences:
 *
 *   - the TC closer fills in the new site (SiteForm in capture mode) and, for a Telco client, the RTS (RequestCreateFields), both
 *     pre-filled from the server's defaults (`rta.completion.defaults`) under what was saved (`completion.values`). Every edit
 *     auto-saves as a SPARSE overlay (lib/rtaCompletion.toValues) with PUT /actions/{id}/rta/completion { values, expectedVersion }
 *     and X-Edit-Session; a stale version (409 stale_completion) shows the latest values and says so on the C-12 card.
 *   - a TC editor who is not a closer, and a view-only user, see the same forms as values (`canEdit` false), with the lock line and
 *     no buttons.
 *   - the client sees the final option alone, never the values (the API sends it none); the status panel says who is finalizing.
 *   - a Completed RTA shows its Final option and Completion Remarks; the panel above carries View site / View RTS.
 *
 * What the forms start from is the API's (`completion.defaults`, C13): Site Name and Site ID are the REQUEST's (Step 1; a Tower Co manager may
 * have set them), the handover status is the approved option's Site Status, every other field the approved option's alone; a kept legacy site's own
 * values win over those. The SPA adds no fallback of its own. It says where a pre-filled value came from ("From the request.", "From option 2.",
 * "From the legacy site record.") for as long as the field still holds it, and only a Tower Co manager edits Site Name and Site ID.
 *
 * The drawer (RtaDetails) owns the commands that read this form: Complete RTA (it calls flush(), precheck(), snapshot() and
 * currentVersion()), and Unlock (it flushes this form first; the entries are kept). This component says `unlock`, and `refused` for a
 * refusal of a save that is not about a field or the version (not_transitioned, request_closed, ...), which the drawer words and acts on.
 *
 * Every gate is lib/rtaStage over `rtaData`, wrapped in `computed`, so it follows the store as it hydrates.
 */

import axios from '@/lib/axiosClient'
import RequestMixins from '@/mixins/RequestMixins'
import RtaCommandMixins from '@/mixins/RtaCommandMixins'

import * as rtaStage from '@/lib/rtaStage'
import * as rc from '@/lib/rtaCompletion'
import * as popup from '@/lib/savePopup'
import { createAutosave } from '@/lib/rtaAutosave'
import { keepalivePut } from '@/lib/keepalive'
import { copyText } from '@/lib/requestSyncCopy'
import { normaliseOrgCategory } from '@/lib/contactRoles'
import { hasRemarksText } from '@/lib/remarksText'
import { ymd } from '@/lib/siteTransition'
import { pushDrawer, revealDrawer } from '@/lib/layerNav'

import ApprovalLockLine from '@/components/requests/ApprovalLockLine'
import ReadOnlyRemarks from '@/components/atoms/ReadOnlyRemarks'
import RtaOptionSummary from '@/components/requests/acquire/RtaOptionSummary'
import SiteForm from '@/components/sites/SiteForm'
import RequestCreateFields from '@/components/requests/RequestCreateFields'

export default {
  name: 'RtaCompletion',

  mixins: [RequestMixins, RtaCommandMixins],

  components: {
    ApprovalLockLine,
    ReadOnlyRemarks,
    RtaOptionSummary,
    SiteForm,
    RequestCreateFields
  },

  props: {

    // The action (GET /actions/{id}, the RTA shape): the object the drawer holds.
    rtaData: {
      type: Object,
      required: true
    },

    // No edits (a record seen from elsewhere, a read-only drawer).
    readOnly: {
      type: Boolean,
      default: false
    },

    // The drawer's lock command in flight: 'lock' | 'unlock' | null.
    lockBusy: {
      type: String,
      default: null
    }

  },


  data () {
    return {
      // The SiteForm value { site, struct, finance, tenancy, candidate, loc } and the RTS form; null until hydrated.
      form: null,
      rts: null,

      // Hydrated and the landowner read (UpdateTenancy reads its value once, when it is created).
      ready: false,
      loadFailed: false,

      // Bumped on every hydration: the selects and the tenancy form read their value once, so a new set of values is a new form.
      formKey: 0,

      // The selected SiteForm tab.
      tab: 'site',

      // Field errors by 'section.field', and the value each had when the error was set (editing the field clears it).
      errors: {},
      errorBase: {},

      // The auto-save's observable state (pending / saving / error).
      autosaveState: null,

      // The version of `completion` this form was last in step with (expectedVersion).
      version: 0,

      // XOL-693: the RTS choice the RTS section was BUILT on (rc.rtsChoiceOf at hydrate), or null for today's RTS form. A deliberate snapshot,
      // like `version` and `pristine`: the form shows what it was built from until the next hydrate. `rtsChoiceLive` is the store's.
      choice: null,

      // The RTA's options: the final option block, and the options Complete will mark Not Selected.
      options: []
    }
  },


  computed: {

    // RequestMixins reads `actionData`: here it is the drawer's object.
    actionData () {
      return this.rtaData
    },

    rta () {
      return this.rtaData
    },

    viewerOrg () {
      return this.org()
    },

    viewerProfile () {
      return this.profile()
    },

    completion () {
      var c = this.rta.completion

      return c && typeof c === 'object' ? c : null
    },

    names () {
      var c = this.rta.client || this.rta.organisation || null

      return {
        telco: (c && (c.name || c.shortName)) || 'the client',
        towerCo: this.towerCoName()
      }
    },

    // XOL-693: the API's `completion.rtsChoice` as it is now (null: a new RTS can be opened), reactive with the drawer's object.
    rtsChoiceLive () {
      return this.tcClient ? null : rc.rtsChoiceOf(this.completion)
    },

    // Is the client a Tower Co itself (then there is no RTS, decision 4)?
    tcClient () {
      var c = this.rta.client || this.rta.organisation || null

      return !!(c && normaliseOrgCategory(c.category) === 'Tower Company')
    },

    // The site behind an OPEN legacy RTA (API DEV-38: `completion.site { id, status, handover, handoverStatus }`), or null.
    keptSite () {
      var c = this.completion
      var site = c && c.site && typeof c.site === 'object' ? c.site : null

      return site && this.rta.status !== 'Closed' ? site : null
    },

    // Decision 108: the kept legacy site has already left Candidate (the nightly job moved it, or a manual edit), so Step 5 shows its
    // current status and handover date as disabled values; the API ignores what is sent for them. Null otherwise.
    transitionedStatus () {
      var site = this.keptSite

      return site && site.status && site.status !== 'Candidate' ? String(site.status) : null
    },

    transitionedHandover () {
      return this.transitionedStatus && this.keptSite.handover ? String(this.keptSite.handover) : null
    },

    // 'completed' | 'client' | 'tower'
    mode () {
      if (this.rta.status === 'Closed') {
        return 'completed'
      }

      return this.isTowerCo() ? 'tower' : 'client'
    },

    // The TC closer, in Finalize, on a drawer that is not read-only.
    canEdit () {
      return !this.readOnly && rtaStage.canEditCompletion(this.rta, this.viewerOrg, this.viewerProfile)
    },

    canUnlock () {
      return !this.readOnly && rtaStage.canUnlockDecision(this.rta, this.viewerOrg, this.viewerProfile)
    },

    readOnlyNote () {
      return rc.readOnlyNote()
    },

    lockRow () {
      var s = this.rta && this.rta.state

      return s && s.rta && s.rta.tc && typeof s.rta.tc === 'object' ? s.rta.tc : {}
    },

    lockBusyText () {
      return this.lockBusy === 'unlock' ? copyText('requestSync.approvalLock.line.unlocking') : copyText('requestSync.approvalLock.line.locking')
    },

    completionRemarks () {
      return hasRemarksText(this.rta.closeReason)
    },

    // The id of the option Finalize was opened on: the one Submit decision bound (`completion.optionId`, Tower Co callers), else the decision's.
    finalOptionId () {
      var c = this.completion
      var tc = rtaStage.tcDecision(this.rta)

      if (c && c.optionId !== null && c.optionId !== undefined) {
        return c.optionId
      }

      if (tc.optionId !== null && tc.optionId !== undefined) {
        return tc.optionId
      }

      return rtaStage.optionSummary(this.rta).approvedOptionId
    },

    // The option row: the bound one; else (the client is sent neither id) the one that is Approved or Final Approved.
    finalOption () {
      var id = this.finalOptionId
      var found = null

      this.options.forEach(function (o) {
        if (!found && id !== null && id !== undefined && Number(o.id) === Number(id)) {
          found = o
        }
      })

      this.options.forEach(function (o) {
        if (!found && (id === null || id === undefined) && (o.optionStatus === 'Approved' || o.optionStatus === 'Final Approved')) {
          found = o
        }
      })

      return found
    },

    // The id View option opens: the row's, else the one the RTA names; none while neither is known.
    finalOptionIdForView () {
      var id = this.finalOption ? this.finalOption.id : this.finalOptionId

      return id === null || id === undefined ? null : Number(id)
    },

    optionNo () {
      var s = rtaStage.optionSummary(this.rta)

      return this.finalOption ? Number(this.finalOption.optionNo) : (s.approvedOptionNo || s.finalOptionNo || null)
    },

    finalRows () {
      return rc.finalOptionRows(this.finalOption, {
        structures: this.getConfig().structures,
        telco: this.names.telco,
        towerCo: this.names.towerCo,
        fmtDate: this.formatDate,
        // The client never reads the Landowner (decision 129), in the waiting view and in a Completed RTA alike (`mode` is 'completed' for both).
        client: !this.isTowerCo(),
        optionNo: this.optionNo
      })
    },



    // The RTA's name: the request's Site Name (rtaStage.rtaTitle; the site's own once Completed).
    requestName () {
      return rtaStage.rtaTitle(this.rta)
    },

    // The name the RTS line shows: what the closer has typed, else the request's.
    siteLabel () {
      return (this.form && this.form.site && this.form.site.siteName) || this.requestName || 'the site'
    },

    // On Finalize, Site Name and Site ID are a Tower Co request manager's (rtaStage.isTcRequestManager; decision 173 made them ordinary request fields on
    // Step 1, which a request manager edits after the first proposal). The closer is always one, so this is never true for a form that can be edited;
    // it keeps the two fields values for anyone else who is ever let into the form.
    identityReadOnly () {
      return !this.canEdit || !rtaStage.isTcRequestManager(this.rta, this.viewerOrg, this.viewerProfile)
    },

    // What the server's defaults hold for the site (C13). Reactive: the drawer's object is replaced when the RTA is read again.
    siteDefaults () {
      var c = this.completion

      return c && c.defaults && c.defaults.site && typeof c.defaults.site === 'object' ? c.defaults.site : {}
    },

    // "Where did this value come from" under a pre-filled field, for as long as the field still holds that value (UX spec 2.8, mock M-27).
    // A kept legacy site's own value wins in the defaults (DEV-55), so it is the legacy record that the note names there.
    siteNameNote () {
      return this.noteFor('siteName', this.keptSite ? rc.NOTE_LEGACY : rc.NOTE_REQUEST)
    },

    siteIdNote () {
      return this.noteFor('siteId', this.keptSite ? rc.NOTE_LEGACY : rc.NOTE_REQUEST)
    },

    handoverStatusNote () {
      var kept = this.keptSite

      if (kept && !rc.sameValue(kept.handoverStatus, null)) {
        return this.noteFor('handoverStatus', rc.NOTE_LEGACY)
      }

      return this.noteFor('handoverStatus', this.optionNo ? 'From option ' + this.optionNo + '.' : 'From the approved option.')
    },

    errorCount () {
      return Object.keys(this.errors).length
    },

    errorTitle () {
      return rc.errorSummary(this.errors)
    },

    tabErr () {
      return rc.tabErrors(this.errors)
    },

    // The RTS fields' errors by field name ('rts.targetDate' -> targetDate).
    rtsErrors () {
      var out = {}
      var errors = this.errors

      Object.keys(errors).forEach(function (k) {
        if (k.indexOf('rts.') === 0) {
          out[k.slice(4)] = errors[k]
        }
      })

      return out
    },

    // The most characters the RTS Summary takes (API DEV-48): the same number the precheck judges by.
    summaryMax () {
      return rc.SUMMARY_MAX
    },

    // XOL-693: the ids that tie the RTS section to its heading and notes. Per instance (as RtaOptionSummary.headingId): two drawers can be mounted.
    rtsDomIds () {
      return { heading: 'rta-rts-heading-' + this._uid, why: 'rta-rts-why-' + this._uid, outcome: 'rta-rts-outcome-' + this._uid }
    },

    // XOL-693: 'link' (the RTS is linked) or 'none' (complete without one), from the form's `linkRtsId`.
    rtsMode () {
      var id = this.rts ? this.rts.linkRtsId : null

      return id === null || id === undefined || id === '' ? 'none' : 'link'
    },

    rtsWhy () {
      return rc.rtsWhyText(this.choice, this.names.telco)
    },

    rtsHint () {
      return rc.rtsHintText(this.rtsMode, this.choice ? this.choice.candidate : null)
    },

    rtsNoneText () {
      return rc.rtsNoneText(this.choice, this.names.telco, rtaStage.rtaReference)
    },

    rtsCardRows () {
      return this.choice && this.choice.candidate ? rc.rtsCandidateRows(this.choice.candidate, this.formatDate) : []
    }

  },


  watch: {

    // The server moved Step 5 under us (a re-read after another closer's save, an Unlock and a new Transition): show it, unless an
    // edit of ours waits, which the next save will have refused.
    'completion.version' (value) {
      if (this.mode === 'tower' && Number(value) !== this.version && !this.busy()) {
        // One re-read can move the version and the RTS choice together. This watcher builds the form (and so updates `choiceKey`) before the key
        // watcher runs, so the notice is posted here, from the key the form was built on.
        this.announceRtsChange()
        this.hydrate()
      }
    },

    // XOL-693: the client's sharer or RTS state on the kept site changed under us (the key is the API's fingerprint of it): read Step 5 again, and
    // say so. Not while an edit waits (the next Complete says so with a 409), and not while a stale_rts_choice is being answered (that
    // refusal has its own notice and re-hydrates itself).
    'completion.rtsChoice.key' () {
      var next = this.rtsChoiceLive
      var key = next ? next.key : null

      if (this.mode !== 'tower' || !this.form || key === this.choiceKey || this.holdRtsWatch || this.busy()) {
        return
      }

      this.announceRtsChange()
      this.hydrate()
    },

    // Finalize began (the answer of Submit decision arrives after the drawer mounted this): read the values.
    'rta.status' () {
      if (this.mode === 'tower' && !this.form && !this.loadFailed) {
        this.hydrate()
      }
    },

    mode (now) {
      if (now === 'tower' && !this.form && !this.loadFailed) {
        this.hydrate()
      }
    }

  },


  methods: {

    /* ---------------------------------------------------------------------
       The values
    --------------------------------------------------------------------- */

    busy () {
      return !!this.autosaveState && (this.autosaveState.pending || this.autosaveState.saving)
    },

    hasPending () {
      return this.busy()
    },

    // The last save was refused and nothing newer waits (XOL-67 B10): the screen holds values the server does not, so a command that
    // reads the server (Complete, Unlock) must not go on until a later save succeeds.
    isRefused () {
      return this.autosave.refused()
    },

    // The effective site as the closer sees it: the form's site with the locality picker's four levels in their columns.
    siteSnapshot () {
      var f = this.form
      var site = Object.assign({}, f.site)
      var loc = f.loc || []

      ;['region', 'state', 'district', 'subdistrict'].forEach(function (k, i) {
        site[k] = this.setProp(site[k], loc[i])
      }, this)

      return site
    },

    // The form as one object of sections, for the overlay and the checks.
    formSections () {
      return {
        site: this.siteSnapshot(),
        structure: this.form.struct,
        finance: this.form.finance,
        tenancy: this.form.tenancy,
        rts: this.tcClient ? null : this.rts
      }
    },

    overlay () {
      return rc.toValues(this.formSections(), this.pristine, this.savedValues, { rtsChoice: !!this.choice })
    },

    // Build the forms from the server's state: its defaults under the saved overlay.
    hydrate () {
      var self = this
      var c = this.completion

      if (!c || !c.defaults || typeof c.defaults !== 'object') {
        // Nothing to build from: read the RTA once (the defaults ride on GET /actions/{id}), then give up with a message.
        if (!this.triedReread) {
          this.triedReread = true

          return this.reread().then(function () {
            return self.completion && self.completion.defaults ? self.hydrate() : self.failLoad()
          })
        }

        this.failLoad()

        return Promise.resolve(false)
      }

      this.triedReread = false

      var eff = rc.effective(c.defaults, c.values)
      var base = rc.effective(c.defaults, {})
      var struct = this.getTableStruct('sites') || {}
      var site = Object.assign({}, struct, eff.site, { status: 'Candidate' })

      // `handoverStatus` is not a key of the struct, and the defaults carry it only when the approved option has a Site Status (C13, decision 153): Vue must see
      // it change either way.
      if (!('handoverStatus' in site)) {
        site.handoverStatus = null
      }
      var pristineSite = Object.assign({}, struct, base.site)

      // Decision 116 (API DEV-55): a kept legacy site that is STILL Candidate may already hold handover values (set on its Site Details,
      // decision 104). Step 5 starts from them; they stay editable, and optional (decision 194). They are the PRISTINE values as well as the
      // form's start (decision 209), not an overlay pre-fill: an untouched field sends nothing (the API falls back to the kept site's own
      // value, the same one), and a field the closer clears differs from its pristine value, so `toValues` sends an explicit null. A stored
      // zero date (0000-00-00) is no date (decision 206), so it starts blank. A value the overlay already holds (saved, even null) wins.
      // The status is the API's default already (DEV-55); it is filled here only when an answer lacks it.
      var kept = this.keptSite

      if (kept && kept.status === 'Candidate') {
        var savedSite = c.values && c.values.site && typeof c.values.site === 'object' ? c.values.site : {}
        var keptDate = ymd(kept.handover) !== '' ? kept.handover : null

        pristineSite.handover = keptDate

        if (!('handover' in savedSite)) {
          site.handover = keptDate
        }

        if (rc.sameValue(pristineSite.handoverStatus, null) && !rc.sameValue(kept.handoverStatus, null)) {
          pristineSite.handoverStatus = kept.handoverStatus

          if (!('handoverStatus' in savedSite)) {
            site.handoverStatus = kept.handoverStatus
          }
        }
      }

      // The tenancy form is built from a partial object (the API's defaults hold only the keys the option filled in: no money, no months,
      // maybe no land type), and UpdateTenancy reads its numbers and labels straight from it: "RM NaN", "NaN months", "Land undefined"
      // (XOL-67 B10). So the keys it lacks come from the tenancy struct, in the form AND in the pristine copy: a key that stands at its
      // struct value in both is not a change, and nothing is sent until the closer edits it (the sparse overlay, decision 75).
      var tenancyStruct = this.getTableStruct('tenancy') || {}
      var tenancy = Object.assign({}, tenancyStruct, eff.tenancy)

      // XOL-693: the RTS section is the link / without choice when the API offers one, else today's RTS form. With a choice the section is
      // the one key `linkRtsId` (the default is the candidate's id: "Link" is pre-selected, decision 213); without one a `linkRtsId` is
      // deleted, in the form and in the pristine copy.
      var choice = this.rtsChoiceLive
      var pristineRts = base.rts
      var formRts = null
      var snapped = false

      if (!this.tcClient && choice) {
        var defaultLink = base.rts && rc.RTS_LINK in base.rts ? base.rts[rc.RTS_LINK] : choice.defaultLinkId

        pristineRts = { linkRtsId: defaultLink }
        formRts = { linkRtsId: eff.rts && rc.RTS_LINK in eff.rts ? eff.rts[rc.RTS_LINK] : defaultLink }
      } else if (!this.tcClient) {
        pristineRts = Object.assign({}, base.rts)
        delete pristineRts[rc.RTS_LINK]
        formRts = Object.assign({}, eff.rts)
        delete formRts[rc.RTS_LINK]
      }

      // The hydrate snap (decision 212): the form never shows a link the server no longer offers. A saved link that is not the candidate
      // becomes the candidate (or "without", with no candidate); with no choice at all it is dropped. Each one queues a save, or the stored
      // overlay would keep the stale id and Complete would refuse it. A saved null ("without") is a valid choice and stays.
      var savedRts = c.values && c.values.rts && typeof c.values.rts === 'object' ? c.values.rts : {}
      var savedHas = rc.RTS_LINK in savedRts
      var savedLink = savedRts[rc.RTS_LINK]

      if (!this.tcClient && savedHas) {
        if (!choice) {
          snapped = true
        } else if (!rc.sameValue(savedLink, null)) {
          if (!choice.candidate) {
            formRts.linkRtsId = null
            snapped = true
          } else if (!rc.sameValue(savedLink, choice.candidate.id)) {
            formRts.linkRtsId = choice.candidate.id
            snapped = true
          }
        }
      }

      this.choice = choice
      this.choiceKey = choice ? choice.key : null
      this.pristine = { site: pristineSite, structure: base.structure, finance: base.finance, tenancy: Object.assign({}, tenancyStruct, base.tenancy), rts: pristineRts }
      this.savedValues = JSON.parse(JSON.stringify(c.values && typeof c.values === 'object' ? c.values : {}))
      this.version = Number(c.version) || 0
      this.lastSiteName = site.siteName
      this.lastOverlay = this.savedValues

      // The form is built from what the server holds: a refusal of an earlier edit is moot (XOL-67 B10).
      if (this.autosave.refused()) {
        this.autosave.cancel()
      }

      this.rts = this.tcClient ? null : formRts
      this.form = {
        site: site,
        struct: eff.structure,
        finance: eff.finance,
        tenancy: tenancy,
        candidate: {},
        loc: [site.region, site.state, site.district, site.subdistrict]
      }
      this.loadFailed = false
      this.ready = false
      this.formKey++

      var form = this.form

      return this.readLandowner(form.tenancy).then(function () {
        if (self.form === form && !self._isDestroyed) {
          self.ready = true

          // Only a closer saves (a viewer's form is never sent), and only a snap that left the form different from what the server holds.
          if (snapped && self.canEdit) {
            self.queue()
          }
        }

        return true
      })
    },

    failLoad () {
      this.form = null
      this.ready = false
      this.loadFailed = true
    },

    retryLoad () {
      this.loadFailed = false
      this.triedReread = false
      this.hydrate()
    },

    // UpdateTenancy shows the landowner it is given and blanks an `owner_id` that has no `owner` beside it. The defaults carry the id;
    // the organisation is read here (one GET) before the form is built.
    readLandowner (tenancy) {
      var self = this

      if (!tenancy || !tenancy.owner_id || tenancy.owner) {
        return Promise.resolve(null)
      }

      return axios.get(this.getEndpoint('/org/' + tenancy.owner_id)).then(function (response) {
        var org = response.data && response.data.data ? response.data.data : response.data

        if (org && typeof org === 'object' && !self._isDestroyed) {
          self.$set(tenancy, 'owner', org)
        }

        return org
      }, function (e) {
        console.error('XOL-67: could not read the landowner of Step 5', e)

        return null
      })
    },

    // The note under a pre-filled site field: `text` while the field still holds what the server's defaults gave it (and that is something), else ''.
    // An edit makes the value the closer's own, so the note goes (mock M-27: a manager's change carries no hint).
    noteFor (field, text) {
      if (!this.form || !this.form.site) {
        return ''
      }

      var base = this.siteDefaults[field]

      if (rc.sameValue(base, null) || !rc.sameValue(this.form.site[field], base)) {
        return ''
      }

      return text
    },

    // The value a field holds now, by 'section.field' (for clearing an error when its field is edited).
    valueOf (key) {
      var parts = String(key).split('.')
      var section = parts[0]
      var field = parts.slice(1).join('.')

      if (!this.form) {
        return undefined
      }

      switch (section) {
        case 'site': return this.siteSnapshot()[field]
        case 'structure': return this.form.struct[field]
        case 'finance': return this.form.finance[field]
        case 'tenancy': return this.form.tenancy[field]
        case 'rts': return this.rts ? this.rts[field] : undefined
      }

      return undefined
    },

    setErrors (errors) {
      var self = this
      var base = {}

      Object.keys(errors).forEach(function (k) {
        base[k] = JSON.stringify(self.valueOf(k))
      })

      this.errors = errors
      this.errorBase = base

      var first = rc.firstErrorTab(errors)

      if (first) {
        this.tab = first
      }
    },

    // An error clears when its field is edited (UX v3 section 6).
    clearEditedErrors () {
      var self = this

      Object.keys(this.errors).forEach(function (k) {
        if (JSON.stringify(self.valueOf(k)) !== self.errorBase[k]) {
          self.$delete(self.errors, k)
          self.$delete(self.errorBase, k)
        }
      })
    },

    onSiteInput () {
      if (!this.canEdit || !this.form || !this.ready) {
        return
      }

      // The RTS summary follows the site name until the closer writes one of their own.
      var name = this.form.site.siteName

      if (name !== this.lastSiteName) {
        if (this.rts && rc.sameValue(this.rts.summary, rc.derivedRtsSummary(this.lastSiteName))) {
          this.rts.summary = rc.derivedRtsSummary(name)
        }

        this.lastSiteName = name
      }

      this.clearEditedErrors()
      this.queue()
    },

    onRtsInput () {
      if (!this.canEdit || !this.form || !this.ready) {
        return
      }

      this.clearEditedErrors()
      this.queue()
    },

    // XOL-693: "Link the existing RTS" / "Complete without an RTS". No confirm: the choice can be changed until Complete.
    onRtsMode (mode) {
      if (!this.canEdit || !this.form || !this.ready || !this.choice || !this.choice.candidate) {
        return
      }

      this.$set(this.rts, rc.RTS_LINK, mode === 'link' ? this.choice.candidate.id : null)
      this.clearEditedErrors()
      this.queue()
    },

    rtsRef (id) {
      return rc.rtsRef(id)
    },

    // View RTS: the RTS's own drawer, one layer up (as RtaDetails' View RTS). Leaving flushes this form first, so the choice is kept.
    viewRts (id) {
      if (this.$router && id) {
        revealDrawer(this.$router, 'request', { id: id })
      }
    },

    // XOL-693: say that the RTS section is about to change under the closer (the form was built on another choice than the drawer holds now).
    // Closers only: a viewer's form is re-drawn silently. Not while a stale_rts_choice is being answered (it has its own card).
    announceRtsChange () {
      var next = this.rtsChoiceLive
      var key = next ? next.key : null

      if (!this.form || !this.canEdit || key === this.choiceKey || this.holdRtsWatch) {
        return
      }

      popup.notify({
        type: 'warning',
        title: 'RTS section changed',
        lead: rc.rtsChangedText(this.choice ? 'choice' : 'form', next ? 'choice' : 'form', this.names.telco),
        replaceKey: 'rta-completion-stale',
        duration: 0
      })
    },

    // Hand the overlay to the auto-save when it says something new. A form that goes back to what was last queued or saved sends nothing.
    queue () {
      var values = this.overlay()

      if (rc.sameOverlay(values, this.lastOverlay)) {
        // Back to what the server holds after a refusal: nothing is left to be refused.
        if (this.autosave.refused()) {
          this.autosave.cancel()
        }

        return
      }

      this.lastOverlay = values
      this.autosave.changed(values)
    },


    /* ---------------------------------------------------------------------
       The auto-save: PUT /actions/{id}/rta/completion (API E18)
    --------------------------------------------------------------------- */

    completionUrl () {
      return '/actions/' + this.rta.id + '/rta/completion'
    },

    // X-Edit-Session (API DEV-17): one id per open drawer, so the saves of a session merge into one Activity row.
    editHeaders () {
      var scope = this.requestSyncScope
      var session = scope && scope.editSession ? scope.editSession : null

      return session ? { 'X-Edit-Session': session } : null
    },

    // Resolves with the answer; REJECTS with the result on a refusal (rtaAutosave reports it once).
    sendValues (values) {
      var body = { values: values, expectedVersion: this.version }

      return this.rtaCommand('put', this.completionUrl(), body, 'Step 5', this.editHeaders()).then(function (result) {
        return result.ok ? result.data : Promise.reject(result)
      })
    },

    // The `completion` of an answer: the envelope's own key, else the one inside its `action`.
    completionOf (data) {
      if (data && data.completion && typeof data.completion === 'object') {
        return data.completion
      }

      var action = data && data.action && typeof data.action === 'object' ? data.action : null

      return action && action.completion && typeof action.completion === 'object' ? action.completion : null
    },

    // The server took the values: its version is ours now, and the drawer follows the answer.
    saved (data, sent) {
      var c = this.completionOf(data)

      this.version = c && c.version !== undefined && c.version !== null ? Number(c.version) : this.version + 1
      this.savedValues = JSON.parse(JSON.stringify(c && c.values && typeof c.values === 'object' ? c.values : sent))

      // The answer's `completion` can sit beside the `action` (which the drawer adopts) instead of inside it: keep the drawer's copy
      // in step, or a later visit to Step 5 would build the form from the old values.
      var inside = data && data.action && data.action.completion
      var merged = c && !inside ? Object.assign({}, this.rta.completion || {}, c) : null

      this.rtaAnnounce(data, this.rta.id)

      if (merged && !this._isDestroyed) {
        this.$set(this.rta, 'completion', merged)
      }
    },

    // A refused save keeps what the closer typed and says why once. A stale version shows the latest values; a field the server named is
    // marked; anything else is the drawer's to word (and to act on: not_transitioned takes it back to Step 4).
    // `opts.command` is 'complete' when it was Complete RTA that was refused (the closer was not editing): the stale card says so.
    refused (result, opts) {
      if (!result || typeof result !== 'object' || !('ok' in result)) {
        return
      }

      var failure = result.failure || {}

      if (failure.code === 'stale_completion') {
        this.autosave.cancel()
        this.showStale(opts && opts.command === 'complete' ? rc.staleCompleteText() : rc.staleText())

        // XOL-693: the re-read can move the RTS choice too. Its "RTS section changed" card shares this card's `replaceKey` and would replace it,
        // so it is held off while the re-read runs (the form is rebuilt from the re-read either way).
        var release = this.releaseRtsWatch

        this.holdRtsWatch = true
        this.showLatest().then(release, release)

        return
      }

      // XOL-693: Complete found the client's sharer or RTS state changed since Step 5 was read. Not an overlay conflict, so the auto-save is
      // left alone. Say so, read Step 5 again (forced: the version may not have moved) and put the closer on the RTS section.
      if (failure.code === 'stale_rts_choice') {
        var self = this

        this.holdRtsWatch = true
        popup.notify({ type: 'warning', title: 'Not completed', lead: rc.rtsStaleText(this.names.telco), replaceKey: 'rta-completion-stale', duration: 0 })

        // An edit that waits is saved FIRST, so the re-read already holds it and the rebuilt form does not show the server's values over
        // the closer's (the queued save would then undo them on the next edit).
        this.autosave.flush().then(function () {
          return self.showLatest({ force: true })
        }).then(this.afterRtsStale, this.afterRtsStale)

        return
      }

      if (this.applyFailure(failure)) {
        return
      }

      this.lastOverlay = this.savedValues
      this.$emit('refused', result, opts)
    },

    releaseRtsWatch () {
      this.holdRtsWatch = false
    },

    afterRtsStale () {
      var self = this

      this.holdRtsWatch = false

      // The re-read failed: the section is still what it was, so the card must not say it shows the latest.
      if (this.lastRereadFailed) {
        popup.notify({ type: 'warning', title: 'Not completed', lead: rc.rtsStaleUnreadText(this.names.telco), replaceKey: 'rta-completion-stale', duration: 0 })
      }

      this.$nextTick(function () {
        var el = self.$refs.rtsSection

        if (el && typeof el.focus === 'function') {
          el.focus()

          if (typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'center' })
          }
        }
      })
    },

    showStale (text) {
      popup.notify({ type: 'warning', title: 'Not saved', lead: text || rc.staleText(), replaceKey: 'rta-completion-stale', duration: 0 })
    },

    // 422 completion_invalid / site_name_taken / site_id_taken: mark the fields, list them above the forms. Returns false when the answer
    // is not one of those or names no field.
    applyFailure (failure) {
      if (['completion_invalid', 'site_name_taken', 'site_id_taken'].indexOf(failure.code) === -1) {
        return false
      }

      var mapped = rc.mapServerErrors(failure)

      if (mapped.count === 0) {
        this.rtaNotifyOnce({ title: 'Not saved', message: rc.invalidFallback(), type: 'warning', duration: 5500 })

        return true
      }

      this.setErrors(mapped.errors)
      this.focusErrors()

      return true
    },

    // The page is being left: what waits goes out as a fetch keepalive request, with the auth headers (DEV-18). With a save in flight and
    // newer values waiting (`ctx.after`), the version is the one that save will make the server hold.
    keepaliveValues (values, ctx) {
      var version = ctx && ctx.after ? this.version + 1 : this.version

      return keepalivePut(this.getEndpoint(this.completionUrl()), { values: values, expectedVersion: version }, this.editHeaders())
    },

    // Their version is the one on screen: read the RTA again; the drawer adopts it (the `completion` watcher builds the form from it), and
    // when the watcher had no reason to (it was busy, or the version did not move) the form is built here.
    // `opts.force` (XOL-693): build it even when the version did not move, as after a stale_rts_choice (the version is not what changed).
    showLatest (opts) {
      var self = this
      var force = !!(opts && opts.force)

      return this.reread().then(function () {
        if (!self._isDestroyed && self.completion && (force || Number(self.completion.version) !== self.version)) {
          return self.hydrate()
        }

        return null
      })
    },

    // Read the RTA again and tell the drawer (its adoption puts the server's `completion` on the object this form reads).
    reread () {
      var self = this
      var id = this.rta.id

      return axios.get(this.getEndpoint('/actions/' + id)).then(function (response) {
        var data = response.data && response.data.data ? response.data.data : response.data

        if (data && typeof data === 'object') {
          self.rtaAnnounce({ action: data }, id)
        }

        self.lastRereadFailed = false

        return data || null
      }).catch(function (e) {
        console.error('XOL-67: re-read of RTA', id, 'failed', e)
        self.lastRereadFailed = true

        return null
      })
    },

    /**
     Save what waits, now. Resolves { ok }: false when a save was refused (its notice is up and the command must not go on).
     */
    flush () {
      if (this.autosave.refused()) {
        return this.answerRefusal()
      }

      return this.autosave.flush().then(function (r) {
        return { ok: !!r.ok }
      })
    },

    /**
     A command (Complete, Unlock) was stopped by the last save's refusal (XOL-67 B10), and must say why rather than do nothing.
     A 422 is the closer's to fix by editing: the refusal is shown again (the fields marked, listed, and focused). Any other refusal (a
     5xx, a network error) says nothing about the values: they are sent once more, and the command goes on if the server takes them.
     */
    answerRefusal () {
      var result = this.autosave.lastRefusal()
      var failure = result && result.failure ? result.failure : null

      if (failure && failure.status === 422) {
        this.refused(result)

        return Promise.resolve({ ok: false })
      }

      return this.autosave.retry().then(function (r) {
        return { ok: !!r.ok }
      })
    },


    /* ---------------------------------------------------------------------
       What Complete RTA reads
    --------------------------------------------------------------------- */

    // The advisory checks (lib/rtaCompletion.precheck). Marks the fields and lists them; { ok, count, errors }.
    precheck () {
      if (!this.form) {
        return { ok: false, count: 1, errors: {} }
      }

      var sections = this.formSections()
      var result = rc.precheck(sections, sections.rts, { skipHandover: !!this.transitionedStatus, rtsChoice: this.choice })

      this.setErrors(result.errors)

      if (result.count > 0) {
        this.focusErrors()
      }

      return { ok: result.count === 0, count: result.count, errors: result.errors }
    },

    // What the Complete confirm names: the site as typed, the RTS target date, the options that will not be selected.
    snapshot () {
      var site = this.form ? this.siteSnapshot() : {}
      var rts = this.rts || {}

      // XOL-693: how Complete will treat the RTS ('opened' with no choice; with one, 'linked' or 'none'; null for a Tower Co client).
      var outcome = this.tcClient ? null : (!this.choice ? 'opened' : (this.rtsMode === 'link' && this.choice.candidate ? 'linked' : 'none'))

      return {
        siteName: site.siteName || this.requestName,
        siteId: site.siteId || '',
        // The handover date (raw) and status the site becomes (decisions 83, 84); the drawer words them and judges the warning.
        // (a stored 0000-00-00 is no date, as on the API)
        handover: ymd(site.handover) !== '' ? site.handover : null,
        handoverStatus: site.handoverStatus || null,
        // Decision 108 / the kept legacy site: an open RTA whose site exists (completion.site).
        kept: !!this.keptSite,
        transitionedStatus: this.transitionedStatus,
        targetDate: rts.targetDate ? this.formatDate(rts.targetDate) : '',
        // The target date is meaningful for 'opened' only. `rtsChoiceKey` is the API's fingerprint of the choice this form was built on
        // (null with none): Complete echoes it as `expectedRtsChoice`.
        rtsOutcome: outcome,
        rtsLink: outcome === 'linked' ? this.choice.candidate : null,
        rtsChoiceKey: this.choiceKey,
        optionNo: this.optionNo,
        others: rc.otherOptionNos(this.options, this.finalOptionId)
      }
    },

    currentVersion () {
      return this.version
    },

    focusErrors () {
      var self = this

      this.$nextTick(function () {
        var a = self.$refs.errorAlert
        var el = a && a.$el ? a.$el : a

        if (el && typeof el.focus === 'function') {
          el.focus()
        }
      })
    },

    focusLine (which) {
      this.focusLockLine(which)
    },


    /* ---------------------------------------------------------------------
       The options (the final option block, Complete's "Not Selected" list)
    --------------------------------------------------------------------- */

    loadOptions () {
      var self = this
      var seq = ++this.optionsSeq

      if (!this.rta || !this.rta.id) {
        return Promise.resolve([])
      }

      return axios.get(this.getEndpoint('/candidates/rta/' + this.rta.id + '?per_page=50')).then(function (response) {
        var body = response ? response.data : null
        var rows = Array.isArray(body) ? body : (body && Array.isArray(body.data) ? body.data : [])

        if (seq === self.optionsSeq && !self._isDestroyed) {
          self.options = rows
        }

        return self.options
      }, function (e) {
        console.error('XOL-67: could not read the options of RTA', self.rta.id, e)

        return []
      })
    },

    // View option: the option's layer, over this drawer. Its gates keep it read-only while the decision is locked.
    viewOption () {
      if (this.finalOption && this.$router) {
        pushDrawer(this.$router, { open: { candidate: { id: Number(this.finalOption.id) } } })
      }
    }

  },


  created () {
    var self = this

    // Not reactive: the auto-save of Step 5. Its `state` is.
    this.autosave = createAutosave({
      send: function (values) { return self.sendValues(values) },
      onSaved: function (data, values) { self.saved(data, values) },
      onRefused: function (result) { self.refused(result) },
      keepalive: function (values, ctx) { return self.keepaliveValues(values, ctx) },
      keepaliveWhileSaving: true
    })
    this.autosaveState = this.autosave.state
    this.optionsSeq = 0
    this.pristine = {}
    this.savedValues = {}
    this.lastOverlay = {}
    this.lastSiteName = null
    this.triedReread = false
    this.choiceKey = null
    this.holdRtsWatch = false
    this.lastRereadFailed = false
  },


  mounted () {
    // Every mode reads the options: the Final option block (Completed too) and, for Complete's confirm, the ones that become Not Selected.
    this.loadOptions()

    if (this.mode === 'tower') {
      this.hydrate()
    }
  },


  beforeDestroy () {
    var save = this.autosave

    // The safety flush: whatever waits is saved now, and the card stops hearing from this form.
    save.flush().then(function () {
      save.destroy()
    })

    this.optionsSeq++
  }

}

</script>


<style scoped>
  .rta-completion h5 { margin: 16px 0 6px; }
  .rta-completion__note { margin: 0 0 8px; }
  .rta-completion__errors { margin: 16px 0 4px; }
  .rta-completion__errors:focus { outline: none; }
  .rta-completion__errors:focus-visible { outline: 2px solid #409EFF; outline-offset: 2px; }
  .rta-completion__no-rts { margin-top: 16px; }
  .rta-completion__failed { margin-top: 12px; }
  .rta-completion__loading { margin: 12px 0; }

  /* The final option: the bordered block of Recipe C. Element's 22px form-item gap is for inputs; these are bold values. */
  .rta-final h5 { margin: 4px 0 8px; }
  .rta-final .bg-bordered { padding: 8px 12px 10px; }
  .rta-final__form >>> .el-form-item { margin-bottom: 0; }
  .rta-final__form >>> .el-form-item__label,
  .rta-final__form >>> .el-form-item__content { line-height: 30px; }

  .rta-rts-line { margin: 4px 0 8px; }

  /* XOL-693: the RTS section (a focus target for a stale choice), the choice's notes, and the linked RTS's card. */
  .rta-rts:focus { outline: none; }
  .rta-rts:focus-visible { outline: 2px solid #409EFF; outline-offset: 2px; }
  .rta-rts-choice__note { display: block; font-size: 12px; line-height: 1.45; color: #606266; max-width: 720px; margin-top: 4px; }
  .rta-rts-choice >>> .el-form-item { margin-bottom: 0; }
  .rta-rts-choice >>> .el-form-item__error { position: static; padding-top: 4px; }
  .rta-rts-card { margin-top: 12px; }
  .rta-rts-none { margin: 8px 0 0; }
  .rta-rts-none__acts { display: block; margin-top: 8px; }
  .rta-view-btn >>> .fa + span { margin-left: 6px; }

  /* Read only: the RTS fields read as values, as SiteForm's do (the text and date atoms already render bold when disabled). */
  .rta-rts-form--readonly >>> .el-input.is-disabled .el-input__inner {
    background: transparent;
    border-color: transparent;
    color: #303133;
    font-weight: bold;
    cursor: default;
    padding-left: 0;
  }
  .rta-rts-form--readonly >>> .el-input__suffix { display: none; }
</style>
